import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
  createStyles,
  Theme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SchoolIcon from "@material-ui/icons/School";
import { makeStyles } from "@material-ui/styles";
import managerClassApi from "api/managerClass";
import EmptyComponent from "components/Empty";
import { InfoUser } from "models/auth";
import { ListResponseDetailClass } from "models/managerClass";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface DetailClassModalProps {
  open: boolean;
  classId: number | string | null;
  classNameText?: string;
  handleClose: () => void;
}

export default function DetailClassModal({
  open,
  classId,
  classNameText,
  handleClose,
}: DetailClassModalProps) {
  const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
      head: {
        padding: "12px 14px",
        backgroundColor: "#F8FAFC",
        color: "#0F172A",
        fontSize: 12,
        fontWeight: 700,
        borderRight: "1px solid #F1F5F9",
        borderBottom: "2px solid #E2E8F0",
      },
      body: {
        padding: "12px 14px",
        fontSize: 12,
        borderRight: "1px solid #F1F5F9",
        borderBottom: "1px solid #F1F5F9",
        fontWeight: 500,
        color: "#334155",
      },
    })
  )(TableCell);

  const useStyles = makeStyles(() => ({
    modalContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(15, 23, 42, 0.5)",
    },
    modalCard: {
      width: 760,
      maxWidth: "94vw",
      maxHeight: "88vh",
      display: "flex",
      flexDirection: "column",
      borderRadius: 24,
      backgroundColor: "#FFFFFF",
      boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
      border: "1px solid #E2E8F0",
      outline: "none",
      overflow: "hidden",
    },
    modalHeader: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      color: "#FFFFFF",
      padding: "18px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0,
    },
    modalBody: {
      padding: "24px",
      overflowY: "auto",
      flex: 1,
    },
    tableContainer: {
      borderRadius: 14,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.03)",
      maxHeight: 440,
    },
    modalFooter: {
      padding: "16px 24px",
      display: "flex",
      justifyContent: "flex-end",
      backgroundColor: "#F8FAFC",
      borderTop: "1px solid #F1F5F9",
      flexShrink: 0,
    },
    btnClose: {
      borderRadius: 10,
      padding: "8px 24px",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      color: "#475569",
      borderColor: "#CBD5E1",
      "&:hover": {
        backgroundColor: "#E2E8F0",
      },
    },
    mobileCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 14,
      border: "1px solid #E2E8F0",
      padding: 14,
      marginBottom: 10,
    },
  }));

  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState<InfoUser[]>([]);
  const [currentClassName, setCurrentClassName] = useState(classNameText || "");

  useEffect(() => {
    if (open && classId) {
      setLoading(true);
      (async () => {
        try {
          const res: ListResponseDetailClass = await managerClassApi.getDetailClass(String(classId));
          setUserList(res?.class?.userList || []);
          setCurrentClassName(res?.class?.name || classNameText || "");
        } catch (error) {
          console.error("Failed to fetch detail class:", error);
          setUserList([]);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open, classId, classNameText]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.modalContainer}
      closeAfterTransition
    >
      <Slide direction="down" in={open} mountOnEnter unmountOnExit>
        <Paper className={classes.modalCard} elevation={0}>
          {/* Header */}
          <Box className={classes.modalHeader}>
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <SchoolIcon style={{ color: "#818CF8", fontSize: 24 }} />
              <Typography variant="h6" style={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.125rem" }}>
                {t("managerClass.danhsachhocvien")} {currentClassName ? `- Lớp ${currentClassName}` : ""}
              </Typography>
              <Chip
                label={`${userList.length} học viên`}
                size="small"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  marginLeft: 8,
                }}
              />
            </Box>
            <IconButton onClick={handleClose} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box className={classes.modalBody}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress size={40} style={{ color: "#2563EB" }} />
              </Box>
            ) : userList.length > 0 ? (
              !isMobile ? (
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell align="center" width="70px">
                          {t("stt")}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {t("admin.tenhocvien")}
                        </StyledTableCell>
                        <StyledTableCell align="center" width="140px">
                          {t("admin.manhanvien")}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {t("admin.email")}
                        </StyledTableCell>
                        <StyledTableCell align="center" width="140px">
                          {t("admin.sodienthoai")}
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userList.map((user: InfoUser, index: number) => (
                        <TableRow key={user.id || index} hover>
                          <StyledTableCell align="center">
                            <Typography style={{ fontWeight: 700, color: "#334155" }}>
                              {index + 1}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Typography style={{ fontWeight: 700, color: "#0F172A" }}>
                              {user.name}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip
                              label={user.staffId || "---"}
                              size="small"
                              style={{
                                backgroundColor: "#F1F5F9",
                                color: "#475569",
                                fontWeight: 700,
                                borderRadius: 6,
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="left">{user.email || "---"}</StyledTableCell>
                          <StyledTableCell align="center">{user.phone || "---"}</StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box>
                  {userList.map((user: InfoUser, index: number) => (
                    <Box key={user.id || index} className={classes.mobileCard}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#0F172A" }}>
                          {user.name}
                        </Typography>
                        <Chip
                          label={user.staffId || "---"}
                          size="small"
                          style={{
                            backgroundColor: "#EEF2FF",
                            color: "#4F46E5",
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" display="block" style={{ color: "#64748B" }}>
                        Email: {user.email || "---"}
                      </Typography>
                      <Typography variant="caption" display="block" style={{ color: "#64748B" }}>
                        SĐT: {user.phone || "---"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )
            ) : (
              <EmptyComponent />
            )}
          </Box>

          {/* Footer */}
          <Box className={classes.modalFooter}>
            <Button variant="outlined" className={classes.btnClose} onClick={handleClose}>
              Đóng
            </Button>
          </Box>
        </Paper>
      </Slide>
    </Modal>
  );
}
